import { bootstrapApplication } from '@angular/platform-browser';
import {RouteReuseStrategy, provideRouter, withPreloading, PreloadAllModules, Router, withNavigationErrorHandler} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { environment } from './environments/environment';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import {provideHttpClient, withInterceptors, withXhr} from "@angular/common/http";
import { register } from 'swiper/element/bundle';
import { AuthInterceptor } from "./app/interceptors/AuthInterceptor";
import {inject, provideAppInitializer, provideZoneChangeDetection} from "@angular/core";
import {AppSettingsService} from "./app/services/app-settings/app-settings.service";
import {AuthTokenStore} from "./app/services/auth-token/auth-token.store";
import {loadGoogleTagManager} from "./app/services/gtm/gtm";
import {TokenService} from "./app/services/token/token.service";
loadGoogleTagManager(environment.gtm);
console.log('[BOOT]', Date.now(), performance.now());

// The name iOS gives the web app when it is added to the Home Screen (it reads this at that moment).
document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', environment.pwaName);
// The browser bar matches the environment: purple (prod), blue (dev) or orange (staging).
document.querySelector('meta[name="theme-color"]')?.setAttribute('content', environment.pwaThemeColour);

// A deploy replaces the lazy-loaded files; a tab (or the service worker's cache) that still points at
// the old ones fails to load a page. Reloading fetches the new version.
function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && (
    error.name === 'ChunkLoadError'
    || /Failed to fetch dynamically imported module/i.test(error.message)
    || /Importing a module script failed/i.test(error.message)
    || /Loading chunk .* failed/i.test(error.message)
  );
}

register();
bootstrapApplication(AppComponent, {
  providers: [
    // Keep zone.js change detection: Angular 21+ defaults new apps to zoneless.
    provideZoneChangeDetection(),
    provideAppInitializer(() => {
      inject(AppSettingsService).load();
    }),
    // Load the auth token from secure storage before anything reads it synchronously.
    // TokenService may already exist (created by AuthInterceptor for earlier startup
    // requests) with a stale signed-out state, so sync it once the token is loaded.
    provideAppInitializer(async () => {
      const tokenStore = inject(AuthTokenStore);
      const tokenService = inject(TokenService);
      await tokenStore.init();
      tokenService.updateToken(!!tokenStore.getToken());
    }),
    // Angular 22+ defaults HttpClient to fetch; keep the XHR backend the app was built on.
    provideHttpClient(
      withXhr(),
      withInterceptors([AuthInterceptor])
    ),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(routes, withPreloading(PreloadAllModules), withNavigationErrorHandler(({ error }) => {
      // At most once every 30 seconds, so a page that is really missing can't reload forever.
      if (isChunkLoadError(error) && Date.now() - Number(sessionStorage.getItem('chunkReloadAt') ?? 0) > 30000) {
        sessionStorage.setItem('chunkReloadAt', String(Date.now()));
        window.location.reload();
      }
    })),
    // The PWA service worker: web builds only, never inside the native apps.
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker && !Capacitor.isNativePlatform(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
});
